import React from 'react';
import { createContext, useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { useAdminGetSession } from 'medusa-react';
import Medusa from '../services/api';
import { PillexaUser } from '../hooks/typings';

interface AuthContextType {
  user?: PillexaUser;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isLoading: false,
  refetch: async () => { },
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<PillexaUser | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user: medusaUser } = useAdminGetSession();

  const getUser = useCallback(async () => {
    if (!medusaUser) return;
    setIsLoading(true);
    try {
      const { data } = await Medusa.users.getUserV2(medusaUser.id as string);
      setUser(data?.user as PillexaUser);
    } finally {
      setIsLoading(false);
    }
  }, [medusaUser]);

  const refetch = useCallback(async () => {
    await getUser();
  }, [getUser]);

  useEffect(() => {
    if (medusaUser) {
      getUser();
    } else {
      setUser(undefined);
    }
  }, [medusaUser, getUser]);

  // Memoize the value to prevent unnecessary re-renders
  const value = useMemo(() => ({ user, isLoading, refetch }), [user, isLoading, refetch]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);